import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomActionBar from '@/domains/mobile-web/components/BottomActionBar/BottomActionBar';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useAddCartItem, useCartCount } from '@/domains/mobile-web/hooks/useCart';
import { isNotFound, useProduct } from '@/domains/mobile-web/hooks/useProducts';
import { authorLabel, categoryLabel } from '@/domains/mobile-web/utils/bundle';
import { flyToCart } from '@/domains/mobile-web/utils/flyToCart';
import { formatPrice, priceView } from '@/domains/mobile-web/utils/price';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import ImageGallery, { type GalleryItem } from '@/shared/components/ui/ImageGallery/ImageGallery';
import useRequireLogin from '@/shared/hooks/useRequireLogin';

interface InfoBookProps {
  title: string;
  desc: string;
}

const InfoBook = ({ title, desc }: InfoBookProps) => {
  return (
    <div className="w-full flex flex-col gap-1">
      <p className="title2 text-text-grey">{title}</p>
      <p className="title2medium text-black">{desc}</p>
    </div>
  );
};

const formatNumber = (value: number) => new Intl.NumberFormat('id-ID').format(value);
const centimetres = (value: string | null) =>
  value === null ? '-' : `${formatNumber(Number(value))} cm`;

const ProductDetail = () => {
  const navigate = useNavigate();
  const requireLogin = useRequireLogin();
  // The route parameter is the product slug
  const { id: slug } = useParams();
  const [showFullDesc, setShowFullDesc] = useState(false);

  const { data: product, isLoading, error } = useProduct(slug);
  const addCartItem = useAddCartItem();
  const cartCount = useCartCount();

  const header = (
    <HeaderMobile>
      <TopBarHeader
        type="textBucket"
        onBack={() => navigate(-1)}
        totalProduct={cartCount}
        title="Detail Buku"
        onAction={() => navigate(`/store/cart`)}
      />
    </HeaderMobile>
  );

  if (isLoading) {
    return <main className="min-h-screen">{header}</main>;
  }

  if (!product) {
    return (
      <>
        {header}
        <WrapperMobile>
          <p className="body1 text-text-grey">
            {!slug || isNotFound(error) ? 'Buku tidak ditemukan.' : 'Gagal memuat buku.'}
          </p>
        </WrapperMobile>
      </>
    );
  }

  const images: GalleryItem[] = product.images.map((url) => ({ url, type: 'image' }));
  const { hasDiscount, original, final } = priceView(product);

  return (
    <main className="min-h-screen">
      {header}

      <WrapperMobile className="mb-6">
        <ImageGallery images={images} showArrows={false} />

        <div className="h-px w-full bg-blue-cs-200" />

        <div className="flex flex-col gap-2">
          {hasDiscount && (
            <p className="text-text-danger-500 title2 line-through">RP{formatPrice(original)}</p>
          )}
          <p className="title1bold text-black">Rp {formatPrice(final)}</p>
          <h1 className="title2bold text-black">{product.title}</h1>
        </div>

        <article className="flex flex-col gap-2">
          <p
            className={showFullDesc ? 'body1 text-text-grey' : 'body1 line-clamp-5 text-text-grey'}
          >
            {product.description}
          </p>
          <button
            type="button"
            onClick={() => setShowFullDesc((prev) => !prev)}
            className="flex items-center gap-1 self-end body2medium text-black"
          >
            {showFullDesc ? 'Tampilkan Lebih Sedikit' : 'Baca Selengkapnya'}
            {showFullDesc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </article>
        <div className="flex flex-col gap-3 pb-6">
          <p className="title1medium text-black">Detail Buku</p>
          <div className="grid grid-cols-2 w-full gap-3">
            <InfoBook title="Nomor ISBN" desc={product.isbn ?? '-'} />
            <InfoBook title="Kategori" desc={categoryLabel(product) ?? '-'} />
            <InfoBook title="Penulis" desc={authorLabel(product) ?? '-'} />
            <InfoBook title="Panjang" desc={centimetres(product.length_cm)} />
            <InfoBook title="Lebar" desc={centimetres(product.width_cm)} />
            <InfoBook
              title="Berat"
              desc={product.weight_grams === null ? '-' : `${formatNumber(product.weight_grams)} g`}
            />
          </div>
        </div>
      </WrapperMobile>
      <BottomActionBar
        className=""
        onAddToCart={(origin) =>
          requireLogin(() => {
            flyToCart({ imageUrl: product.images[0], from: origin });
            addCartItem.mutate({ productId: product.id });
          })
        }
        onBuyNow={() =>
          requireLogin(() =>
            navigate(`/store/checkout?product=${encodeURIComponent(product.slug)}`)
          )
        }
      />
    </main>
  );
};

export default ProductDetail;
